<template>
  <div class="speedtest">
    <span class="title">{{ iface }}: {{ mode }}</span>
    <div class="speedtest__wrap">
      <div class="speedtest__speedometr">
        <span class="s-0">0</span>
        <span class="s-1">1</span>
        <span class="s-5">5</span>
        <span class="s-10">10</span>
        <span class="s-20">20</span>
        <span class="s-30">30</span>
        <span class="s-50">50</span>
        <span class="s-100">100</span>
        <span class="s-200">200</span>
        <div class="speedtest_triangle"></div>
      </div>

      <div class="speedtest__strelka" :style="strelkaStyle" :class="{ active: tremor }">
        <div class="strelka"></div>
      </div>

      <div class="speedtest__circle" :class="{ active: tremor }">
        <svg class="progress-ring" width="280" height="280" :style="speedometerFillingStyle">
          <defs>
            <linearGradient id="gradient" x1="25%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#cdf3d1" />
              <stop offset="50%" stop-color="#84e28d" />
              <stop offset="100%" stop-color="rgba(0, 162, 84, 0.5)" />
            </linearGradient>
          </defs>
          <circle
              class="progress-ring__circle"
              stroke="url(#gradient)"
              stroke-width="28"
              cx="140"
              cy="140"
              :r="radius"
              fill="none"
          />
        </svg>
      </div>

      <span class="speedtest__text">
        {{ speedDisplay }} <b>{{ translate("speed.Mbs") }}</b>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SpeedTest',
  props: {
    speed: { type: Number, required: true },
    iface: { type: String, default: '' },
    mode: { type: String, default: '' }
  },
  data() {
    return {
      tremor: false,
      radius: 125,
      segments: [
        { min: 0,   max: 1,   sumStr: 38,  oneStr: 38,   sumCur: 10.1, oneCur: 10 },
        { min: 1,   max: 5,   sumStr: 38,  oneStr: 9.5,  sumCur: 10.1, oneCur: 2.5 },
        { min: 5,   max: 10,  sumStr: 38,  oneStr: 7,    sumCur: 10.1, oneCur: 2 },
        { min: 10,  max: 30,  sumStr: 63,  oneStr: 3.1,  sumCur: 20.2, oneCur: 1 },
        { min: 30,  max: 50,  sumStr: 38,  oneStr: 1.9,  sumCur: 11,   oneCur: 0.55 },
        { min: 50,  max: 100, sumStr: 38,  oneStr: 0.76, sumCur: 10,   oneCur: 0.2 },
        { min: 100, max: 200, sumStr: 33,  oneStr: 0.33, sumCur: 10,   oneCur: 0.1 }
      ]
    }
  },
  computed: {
    // Вычисляем скорость в Мб/с (число)
    speedMb() {
      return this.speed / 1024 / 1024;
    },
    // Для отображения с двумя знаками после запятой
    speedDisplay() {
      return this.speedMb.toFixed(2);
    },
    // Вычисляем стиль для стрелки (поворот)
    strelkaStyle() {
      const speed = this.speedMb;
      const finalDeg = this.segments.reduce((deg, segment) => {
        if (speed > segment.max) {
          return deg + segment.sumStr;
        } else if (speed >= segment.min && speed <= segment.max) {
          return deg + (speed - segment.min) * segment.oneStr;
        }
        return deg;
      }, -55);
      return `transform: rotate(${finalDeg}deg);`;
    },
    // Вычисляем процент заполнения круга
    circleProgress() {
      const speed = this.speedMb;
      return this.segments.reduce((progress, segment) => {
        if (speed > segment.max) {
          return progress + segment.sumCur;
        } else if (speed >= segment.min && speed <= segment.max) {
          return progress + (speed - segment.min) * segment.oneCur;
        }
        return progress;
      }, 0);
    },
    // Вычисляем стиль для SVG-круга заполнения
    speedometerFillingStyle() {
      const circumference = 2 * Math.PI * this.radius;
      const offset = circumference - (this.circleProgress / 100) * circumference;
      return `stroke-dashoffset: ${offset}; stroke-dasharray: ${circumference};`;
    }
  }
}
</script>

<style scoped lang="scss">
.speedtest {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 1;
  transition: all 1s;

  .title {
    font-family: "Roboto", sans-serif;
    color: #252422;
    font-size: 17px;
    margin-bottom: 10px;
    text-align: center;
  }

  &__wrap {
    width: 280px;
    height: 300px;
    overflow: hidden;
    background-color: #f8f8f8;
    position: relative;
  }

  &__speedometr {
    width: 280px;
    height: 280px;
    border: 29px solid #e1e1e1;
    border-radius: 50%;
    position: relative;
    animation: speedo 2s;

    .speedtest_triangle {
      border-left: 110px solid transparent;
      border-right: 110px solid transparent;
      border-bottom: 150px solid #f8f8f8;
      position: absolute;
      bottom: -35px;
      left: calc(50% - 110px);
      z-index: 1;
    }

    & > span {
      color: #252422;
      font-size: 17px;
      position: absolute;
      font-family: "Roboto", Arial, sans-serif;
      font-weight: bold;
      z-index: 3;

      &.s-0 { top: 184px; left: 50px; }
      &.s-1 { top: 138px; left: 18px; }
      &.s-5 { top: 77px;  left: 16px; }
      &.s-10 { top: 28px; left: 46px; }
      &.s-20 { top: 5px;  left: 104px; }
      &.s-30 { top: 28px; right: 46px; }
      &.s-50 { top: 77px; right: 14px; }
      &.s-100 { top: 138px; right: 13px; }
      &.s-200 { top: 184px; right: 45px; }
    }
  }

  &__strelka {
    width: 80px;
    height: 8px;
    position: absolute;
    left: calc(50% - 80px);
    top: calc(50% + 8px);
    transform: rotate(-55deg);
    transform-origin: 100%;
    transition: 1s;
    z-index: 2;
    animation: speedo 2s;

    .strelka {
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, #84e28d, #f8f8f8);
      border-radius: 50% 1px 1px 50%;
      position: absolute;
      top: 0;
      left: 0;
      transform-origin: 100% 100%;
    }
  }

  &__circle {
    position: absolute;
    top: 0;
    left: 0;
    animation: speedo 2s;

    svg {
      transition: 1s;
    }

    .progress-ring__circle {
      transform-origin: center;
      transform: rotate(125deg);
      transition: stroke-dashoffset 0.05s;
    }
  }

  &__text {
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    font-family: "Roboto", Arial, sans-serif;
    font-weight: bold;
    color: #252422;
    font-size: 25px;
    line-height: 120%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;

    b {
      font-size: 14px;
      margin-left: 5px;
    }
  }
}

@keyframes speedo {
  0% { opacity: 0; }
  30% { opacity: 0; }
  100% { opacity: 1; }
}

//@keyframes strelka {
//  0% { transform: rotate(0deg); }
//  50% { transform: rotate(2deg); }
//  100% { transform: rotate(-2deg); }
//}
</style>
